import React from "react";
import { resolveTenantBrand } from "../config/tenantBranding";
import { useGatewayState } from "./GatewayStateMachine";
import GatewayArrival from "./GatewayArrival";
import GatewayScene from "./GatewayScene";
import BrandHomeLink from "./BrandHomeLink";
import "./gateway.css";

const stageNarrative = {
  "identity-entry": { eyebrow: "CLIKKLE IDENTITY", headline: "One identity. Every destination.", body: "Continue securely to the platform that brought you here." },
  "identity-recognition": { eyebrow: "IDENTITY RECOGNIZED", headline: "Welcome back.", body: "Your trusted identity is ready to continue." },
  "password-verification": { eyebrow: "SECURE VERIFICATION", headline: "Protected by design.", body: "Verify your identity to continue to your requested destination." },
  registration: { eyebrow: "CREATE YOUR CLIKKLE ID", headline: "One identity for every platform.", body: "Create your account without leaving the Identity Gateway." },
  recovery: { eyebrow: "ACCOUNT RECOVERY", headline: "Return securely.", body: "Recover access through one protected identity flow." },
};

const focusedStages = new Set(["identity-entry", "identity-recognition", "password-verification", "security-verification", "registration", "recovery"]);

export default function GatewayShell({
  stage,
  children,
  destinationLabel,
  title,
  body,
  arrival = false,
  open = true,
  onOpen,
  onBack,
  onDismiss,
  focused,
  layout = "dialog",
}) {
  const brand = resolveTenantBrand();
  const machine = useGatewayState();
  const activeStage = stage || machine.state;
  const narrative = stageNarrative[activeStage] || brand.copy;
  const progress = Math.max(8, Math.round((machine.stageIndex / machine.totalStages) * 100));
  const isOpen = !arrival || open;
  const isFocused = isOpen && (focused ?? focusedStages.has(activeStage));
  const dismiss = onDismiss || onBack;

  const handleBackdropPointerDown = (event) => {
    if (event.target === event.currentTarget && dismiss) dismiss();
  };

  return (
    <main
      className={`gateway${isOpen ? " gateway--open" : " gateway--arrival"}${isFocused ? " gateway--focused" : ""}`}
      data-stage={activeStage}
      style={{ "--tenant-accent": brand.accent, "--tenant-warm": brand.accentWarm }}
    >
      <GatewayScene open={isOpen} />

      {!isOpen ? (
        <GatewayArrival brand={brand} destinationLabel={destinationLabel} onEnter={onOpen} />
      ) : isFocused ? (
        <section
          className="gatewayModal"
          aria-label={`${brand.name} identity ${activeStage}`}
          onMouseDown={handleBackdropPointerDown}
        >
          <div className={`gateway__panel gateway__panel--focused${layout === "wide" ? " gateway__panel--expansive" : ""}`} role="dialog" aria-modal="true">
            {dismiss ? (
              <button className="gateway__back" type="button" onClick={dismiss} aria-label="Return to arrival">
                <span aria-hidden="true">‹</span>
              </button>
            ) : null}
            <BrandHomeLink brand={brand} className="gateway__panelBrand" ariaLabel={`Go to ${brand.name} home`}>
              <img src={brand.lockup} alt={brand.wordmark} />
            </BrandHomeLink>
            {children}
          </div>
        </section>
      ) : (
        <>
          <header className="gateway__topbar">
            <BrandHomeLink brand={brand} className="gateway__brand" ariaLabel={`Go to ${brand.name} home`}>
              <img src={brand.lockup} alt={brand.wordmark} />
            </BrandHomeLink>
            <div className="gateway__trust"><span /> Protected by {brand.productName}</div>
          </header>
          <section className="gateway__stage" aria-label={`${brand.name} identity ${activeStage}`}>
            <div className="gateway__intro">
              <p className="gateway__eyebrow">{narrative.eyebrow}</p>
              <h1>{title || narrative.headline}</h1>
              <p>{body || narrative.body}</p>
              {destinationLabel ? (
                <div className="gateway__destination"><span>Continuing to</span><strong>{destinationLabel}</strong></div>
              ) : null}
              <div className="gateway__progress" aria-label={`Gateway progress ${progress}%`}><span style={{ width: `${progress}%` }} /></div>
            </div>
            <div className="gateway__panel">
              {arrival && onBack ? <button className="gateway__back" type="button" onClick={onBack} aria-label="Return to arrival"><span aria-hidden="true">‹</span></button> : null}
              {children}
            </div>
          </section>
          <footer className="gateway__footer">
            <span>© {new Date().getFullYear()} {brand.name} Technologies · Identity Gateway V1</span>
            <nav aria-label="Legal"><a href={brand.privacyUrl}>Privacy</a><a href={brand.termsUrl}>Terms</a><a href={brand.supportUrl}>Support</a></nav>
          </footer>
        </>
      )}
    </main>
  );
}
